import { useState, useCallback, useMemo } from 'react';
import JSZip from "jszip";
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { useOptimizedProgress } from './useOptimizedProgress';
import { useAPI } from './useAPIs';
import { useFileApi } from './useFileApi';
import { getSettingValue } from '../utils/CommonUtils';


const DOWNLOAD_PROGRESS_WEIGHT = 0.9;

const BLOB_ACCOUNT = getSettingValue('REACT_APP_BLOB_ACCOUNT');
const isBlobStorageUrl = (url) => url && url.includes(BLOB_ACCOUNT);

export const useFileDownload = (data) => {
  const [downloading, setDownloading] = useState(false);
  const [isZipping, setIsZipping] = useState(false);
  const [progress, setProgressOptimized] = useOptimizedProgress();
  const [error, setError] = useState(null);
  const { downloadFromUrl, getBlobFromUrl } = useAPI();
  const { getDownloadUrl, getMultipleDownloadUrls, invalidateCache } = useFileApi();

  const resolveFileLocation = useCallback((row) => {
    return row?.file_location || row?.file_path || row?.path || row?.location || row?.filename || row?.name || '';
  }, []);

  const rowIdToFilename = useMemo(() => {
    const mapping = {};
    if (data) {
      data.forEach(row => {
        const rowId = row.id ?? `${row.vin}-${row.datetime}`;
        mapping[rowId] = resolveFileLocation(row);
      });
    }
    return mapping;
  }, [data, resolveFileLocation]);

  const getSelectedFilenames = useCallback((rowSelection) => {
    return Object.keys(rowSelection).map(rowId => rowIdToFilename[rowId]).filter(Boolean);
  }, [rowIdToFilename]);

  const downloadSingleFile = useCallback(async (fileLocation) => {
    try {
      setError(null);
      setDownloading(true);
      
      const downloadUrl = await getDownloadUrl(fileLocation);
      const filename = fileLocation.split('/').pop();
      
      try {
        await downloadFromUrl(downloadUrl, filename, setProgressOptimized);
      } catch (dlError) {
        // If SAS token expired (403/401), get a fresh URL and retry once
        if (dlError.message && (dlError.message.includes('403') || dlError.message.includes('401'))) {
          invalidateCache(fileLocation);
          const freshUrl = await getDownloadUrl(fileLocation);
          await downloadFromUrl(freshUrl, filename, setProgressOptimized);
        } else {
          throw dlError;
        }
      }
    } catch (error) {
      console.error('Error downloading single file:', error);
      throw new Error(`Failed to download file: ${error.message}`);
    } finally {
      setDownloading(false);
      setProgressOptimized(0);
    }
  }, [downloadFromUrl, getDownloadUrl, invalidateCache, setProgressOptimized]);

  const downloadMultipleFiles = useCallback(async (filenames, onFileStatusChange) => {
    const zip = new JSZip();
    const totalFiles = filenames.length;
    const fileProgress = {};

    try {
      const fileUrls = await getMultipleDownloadUrls(filenames);
      const urlMap = new Map(fileUrls.map(f => [f.path, f.url]));
      const isNonBlobUrl = (url) => url && !isBlobStorageUrl(url);
      const redirectEntries = [];
      const blobEntries = [];

      filenames.forEach((file, index) => {
        const url = urlMap.get(file);
        if (!url) {
          onFileStatusChange(index, 'error');
          return;
        }
        if (isNonBlobUrl(url)) {
          redirectEntries.push({ index, file, url });
        } else {
          blobEntries.push({ index, file, url });
        }
      });

      let successfulDownloads = 0;
      
      if (blobEntries.length > 0) {
        const BATCH_SIZE = 5;
        const allResults = [];
        for (let i = 0; i < blobEntries.length; i += BATCH_SIZE) {
          const batch = blobEntries.slice(i, i + BATCH_SIZE);
          const batchResults = await Promise.all(batch.map(async ({ index, file, url }) => {
            try {
              onFileStatusChange(index, 'downloading');
              const blob = await getBlobFromUrl(url, (progress) => {
                fileProgress[file] = progress / 100;
                const totalProgress = Object.values(fileProgress).reduce((sum, p) => sum + p, 0);
                const overallProgress = Math.round((totalProgress / totalFiles) * 100 * DOWNLOAD_PROGRESS_WEIGHT);
                setProgressOptimized(overallProgress);
              });
              onFileStatusChange(index, 'completed');
              return { file, blob };
            } catch (error) {
              onFileStatusChange(index, 'error');
              console.error(`Failed to download ${file}:`, error);
              return null;
            }
          }));

          allResults.push(...batchResults);
        }

        allResults.forEach(result => {
          if (result) {
            const baseFileName = result.file.split('/').pop().split('?')[0];
            zip.file(baseFileName, result.blob);
            successfulDownloads++;
          }
        });

        if (successfulDownloads > 0) {
          setIsZipping(true);
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          const timestamp = format(new Date(), 'yyyy-MM-dd_HH-mm');
          saveAs(zipBlob, `${timestamp}_sdl.zip`);
          setIsZipping(false);
        }
      }

      // FilesAPI SAS URLs: use hidden iframes for browser-native downloads.
      if (redirectEntries.length > 0) {
        const iframesToClean = [];
        for (let i = 0; i < redirectEntries.length; i++) {
          const { index, file, url } = redirectEntries[i];
          try {
            onFileStatusChange(index, 'downloading');
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            iframesToClean.push(iframe);
            onFileStatusChange(index, 'completed');
            successfulDownloads++;            
            // Stagger creation so the browser doesn't coalesce multiple downloads.
            if (i < redirectEntries.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 800));
            }
          } catch (error) {
            onFileStatusChange(index, 'error');
            console.error(`Failed to download ${file}:`, error);
          }
        }
        // Clean up iframes after a generous timeout (downloads have already started).
        setTimeout(() => {
          iframesToClean.forEach(iframe => {
            try { document.body.removeChild(iframe); } catch (_) { /* already removed */ }
          });
        }, 120000);
      }

      if (successfulDownloads === 0) {
        throw new Error('All files failed to download.');
      }
      if (successfulDownloads < totalFiles) {
        throw new Error(`Partial failure: ${totalFiles - successfulDownloads} files failed to download.`);
      }
    } catch (error) {
      setIsZipping(false);
      throw error;
    }
  }, [getBlobFromUrl, getMultipleDownloadUrls, setProgressOptimized]);

  const downloadByFilenames = useCallback(async (filenames, onFileStatusChange = () => {}) => {
    if (!filenames || filenames.length === 0) {
      setError('No files selected for download');
      return;
    }

    setDownloading(true);
    setIsZipping(false);
    setProgressOptimized(0);
    setError(null);
    try {
      if (filenames.length > 1) {
        await downloadMultipleFiles(filenames, onFileStatusChange);
      } else {
        onFileStatusChange(0, 'downloading');
        await downloadSingleFile(filenames[0]);
        onFileStatusChange(0, 'completed');
      }
    } catch (error) {
      const message = error.message || 'Failed to download files. Please try again.';
      setError(message);
      throw error;
    } finally {
      setDownloading(false);
      setIsZipping(false);
      setProgressOptimized(0);
    }
  }, [downloadMultipleFiles, downloadSingleFile, setProgressOptimized]);

  return {
    downloading,
    isZipping,
    progress,
    downloadByFilenames,
    getSelectedFilenames,
    downloadSingleFile,
    error,
  };
};
